// frontend/packages/business-console/src/components/media/MediaLibraryPageView.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';

import MediaToolbar from './MediaToolbar';
import MediaGrid from './MediaGrid';
import MediaDetailsDrawer from './MediaDetailsDrawer';
import RecentlyUsedRow from './RecentlyUsedRow';
import UploadQueuePanel from './UploadQueuePanel';
import BulkTagDialog from './BulkTagDialog';
import { MetricBarCards } from '../primitives';

import useBulkMediaUpload from '../../hooks/useBulkMediaUpload';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { toCldToken } from '../../utils/mediaToken';
import {
  deleteMediaAsset,
  listAllMediaAssets,
  updateMediaAsset,
} from '../../services/mediaService';

const normalizeTypeFilter = (tabValue) => {
  if (tabValue === 'video') return 'video';
  if (tabValue === 'image') return 'image';
  return ''; // all
};

// Backend list endpoint only supports type+search, so folder/tag filtering is
// applied client-side over the fetched page. Folder uses case-insensitive
// substring matching. Tag filter is OR across user-entered tags ("logo, hero"
// shows assets tagged either logo OR hero) — narrowing further would frustrate
// users since two tags often hide all results when no asset has both.
function applyClientFilters(items, folder, tags) {
  const folderQuery = String(folder || '')
    .trim()
    .toLowerCase();
  const tagList = Array.isArray(tags)
    ? tags.map((t) => String(t).trim().toLowerCase()).filter(Boolean)
    : [];
  if (!folderQuery && tagList.length === 0) return items;

  return items.filter((it) => {
    if (folderQuery) {
      const assetFolder = String(it?.folder || '').toLowerCase();
      if (!assetFolder.includes(folderQuery)) return false;
    }
    if (tagList.length) {
      const assetTags = (Array.isArray(it?.tags) ? it.tags : []).map((t) =>
        String(t).toLowerCase()
      );
      const anyMatch = tagList.some((q) =>
        assetTags.some((a) => a.includes(q))
      );
      if (!anyMatch) return false;
    }
    return true;
  });
}

const assetId = (asset) => String(asset?.id || asset?._id || '');

// The backend maps the Cloudinary public_id onto blobPath (same as the
// details drawer reads it).
const assetToken = (asset) => toCldToken(String(asset?.blobPath || '').trim());

function uniqueSorted(values) {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  );
}

export default function MediaLibraryPageView() {
  const [typeTab, setTypeTab] = useState('all'); // all | image | video
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState('');
  const [tags, setTags] = useState([]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [toast, setToast] = useState('');
  const fileInputRef = useRef(null);
  const dragDepth = useRef(0);

  const {
    queue: uploadQueue,
    busy: uploading,
    addFiles,
    retry: retryUpload,
    clearFinished: clearUploads,
  } = useBulkMediaUpload();

  const effectiveType = useMemo(() => normalizeTypeFilter(typeTab), [typeTab]);
  // Refetch when the operator stops typing, not on every keystroke.
  const debouncedSearch = useDebouncedValue(search, 300);

  const displayedItems = useMemo(
    () => applyClientFilters(items, folder, tags),
    [items, folder, tags]
  );

  const folderOptions = useMemo(
    () => uniqueSorted(items.map((it) => String(it?.folder || ''))),
    [items]
  );

  const tagOptions = useMemo(
    () =>
      uniqueSorted(
        items.flatMap((it) =>
          (Array.isArray(it?.tags) ? it.tags : []).map(String)
        )
      ),
    [items]
  );

  // Library stats (design Page 9 strip). Derived from the loaded list only,
  // so every number is real: no fabricated storage cap, AI-tagged, or
  // used-on-site totals (those aren't in the list response).
  const stats = useMemo(() => {
    const total = items.length;
    let images = 0;
    let videos = 0;
    let needAlt = 0;
    for (const it of items) {
      const ty = String(it?.type || it?.mediaType || 'image').toLowerCase();
      if (ty === 'video') videos += 1;
      else images += 1;
      if (ty !== 'video' && !String(it?.altText || '').trim()) needAlt += 1;
    }
    return { total, images, videos, needAlt };
  }, [items]);

  const statCards = useMemo(
    () => [
      { label: 'Assets', value: stats.total, bar: 'blue' },
      { label: 'Images', value: stats.images, bar: 'blue' },
      { label: 'Videos', value: stats.videos, bar: 'purple' },
      { label: 'Need alt text', value: stats.needAlt, bar: 'amber' },
    ],
    [stats]
  );

  const refresh = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const result = await listAllMediaAssets({
        type: effectiveType,
        search: debouncedSearch,
      });
      setItems(Array.isArray(result?.items) ? result.items : []);
    } catch (e) {
      setError(e?.message || String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveType, debouncedSearch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refresh once a batch finishes (not per file), so the grid doesn't
  // reflow under the operator while uploads are still landing.
  const wasUploading = useRef(false);
  useEffect(() => {
    if (wasUploading.current && !uploading) refresh();
    wasUploading.current = uploading;
  }, [uploading, refresh]);

  const onPickFiles = useCallback(() => {
    setError('');
    fileInputRef.current?.click();
  }, []);

  const onFilesChosen = useCallback(
    (e) => {
      addFiles(e.target.files);
      // Reset so choosing the same files again still fires onChange.
      e.target.value = '';
    },
    [addFiles]
  );

  const hasFiles = (e) =>
    Array.from(e.dataTransfer?.types || []).includes('Files');

  const onDragEnter = useCallback((e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    if (!hasFiles(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  }, []);

  const onDragOver = useCallback((e) => {
    if (hasFiles(e)) e.preventDefault();
  }, []);

  const onDrop = useCallback(
    (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      setError('');
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const copyTokens = useCallback(async (tokens) => {
    const list = tokens.filter(Boolean);
    if (!list.length) return;
    try {
      await navigator.clipboard.writeText(list.join('\n'));
      setToast(
        `Copied ${list.length} cld: ${list.length === 1 ? 'token' : 'tokens'}`
      );
    } catch {
      setError('Could not copy to the clipboard.');
    }
  }, []);

  const toggleSelect = useCallback((asset) => {
    const id = assetId(asset);
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  const selectedAssets = useMemo(
    () => items.filter((it) => selectedIds.has(assetId(it))),
    [items, selectedIds]
  );

  const allShownSelected =
    displayedItems.length > 0 &&
    displayedItems.every((it) => selectedIds.has(assetId(it)));

  const toggleSelectAllShown = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (displayedItems.every((it) => next.has(assetId(it)))) {
        displayedItems.forEach((it) => next.delete(assetId(it)));
      } else {
        displayedItems.forEach((it) => next.add(assetId(it)));
      }
      return next;
    });
  }, [displayedItems]);

  const applyBulkTags = useCallback(
    async (newTags) => {
      setError('');
      const failures = [];
      for (const asset of selectedAssets) {
        const existing = Array.isArray(asset?.tags) ? asset.tags.map(String) : [];
        const merged = Array.from(new Set([...existing, ...newTags]));
        if (merged.length === existing.length) continue;
        try {
          // eslint-disable-next-line no-await-in-loop
          await updateMediaAsset(assetId(asset), { tags: merged });
        } catch (e) {
          failures.push(e?.message || String(e));
        }
      }
      setTagDialogOpen(false);
      await refresh();
      if (failures.length) {
        setError(
          `${failures.length} of ${selectedAssets.length} assets could not be tagged: ${failures[0]}`
        );
      } else {
        setToast(
          `Tagged ${selectedAssets.length} ${selectedAssets.length === 1 ? 'asset' : 'assets'}`
        );
      }
    },
    [refresh, selectedAssets]
  );

  const openDetails = useCallback((asset) => {
    setSelected(asset || null);
    setDrawerOpen(true);
  }, []);

  const closeDetails = useCallback(() => {
    setDrawerOpen(false);
    setSelected(null);
  }, []);

  const handleSaveMeta = useCallback(
    async (assetId, payload) => {
      setError('');
      try {
        const saved = await updateMediaAsset(assetId, payload);
        await refresh();
        return saved;
      } catch (e) {
        setError(e?.message || String(e));
        return null;
      }
    },
    [refresh]
  );

  const handleDelete = useCallback(
    async (assetId) => {
      setError('');
      try {
        await deleteMediaAsset(assetId);
        closeDetails();
        await refresh();
      } catch (e) {
        setError(e?.message || String(e));
      }
    },
    [closeDetails, refresh]
  );

  return (
    <Box
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: '-0.6px',
                color: 'text.primary',
              }}
              noWrap
            >
              Media
            </Typography>
            <Typography
              sx={{ fontSize: 13.5, color: 'text.secondary', mt: 0.5 }}
              noWrap
            >
              {loading
                ? 'Upload, organize, and reuse images and videos across your site.'
                : stats.needAlt > 0
                  ? `${stats.total} ${stats.total === 1 ? 'asset' : 'assets'} · ${stats.needAlt} need alt text`
                  : `${stats.total} ${stats.total === 1 ? 'asset' : 'assets'}`}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant={selectMode ? 'contained' : 'outlined'}
              color={selectMode ? 'secondary' : 'primary'}
              startIcon={<CheckBoxOutlinedIcon />}
              sx={{ fontWeight: 900 }}
              onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
            >
              {selectMode ? 'Done' : 'Select'}
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              sx={{ fontWeight: 900 }}
              onClick={onPickFiles}
            >
              Upload
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              hidden
              onChange={onFilesChosen}
            />
          </Stack>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <UploadQueuePanel
          queue={uploadQueue}
          busy={uploading}
          onRetry={retryUpload}
          onClear={clearUploads}
          onCopy={copyTokens}
        />

        {!loading && stats.total > 0 ? (
          <MetricBarCards cards={statCards} />
        ) : null}

        <Box>
          <Tabs
            value={typeTab}
            onChange={(_e, v) => setTypeTab(v)}
            sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Tab value="all" label="All" />
            <Tab value="image" label="Images" />
            <Tab value="video" label="Videos" />
          </Tabs>
        </Box>

        <RecentlyUsedRow onSelect={openDetails} />

        <MediaToolbar
          search={search}
          onSearch={setSearch}
          folder={folder}
          onFolder={setFolder}
          folderOptions={folderOptions}
          tags={tags}
          onTags={setTags}
          tagOptions={tagOptions}
          onRefresh={refresh}
        />

        <Divider />

        <Typography variant="caption" sx={{ opacity: 0.6 }}>
          {loading
            ? 'Loading…'
            : displayedItems.length === items.length
              ? `${displayedItems.length} ${displayedItems.length === 1 ? 'asset' : 'assets'}`
              : `${displayedItems.length} of ${items.length} ${items.length === 1 ? 'asset' : 'assets'}`}
        </Typography>

        {selectMode ? (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            spacing={1}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              bgcolor: 'background.paper',
              py: 1,
            }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: 14, mr: 1 }}>
              {selectedIds.size} selected
            </Typography>
            <Button size="small" onClick={toggleSelectAllShown} sx={{ fontWeight: 800 }}>
              {allShownSelected ? 'Clear shown' : `Select all shown (${displayedItems.length})`}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              disabled={!selectedAssets.length}
              onClick={() => copyTokens(selectedAssets.map(assetToken))}
              sx={{ fontWeight: 800 }}
            >
              Copy cld: tokens
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<LocalOfferOutlinedIcon />}
              disabled={!selectedAssets.length}
              onClick={() => setTagDialogOpen(true)}
              sx={{ fontWeight: 800 }}
            >
              Add tags
            </Button>
          </Stack>
        ) : null}

        <MediaGrid
          loading={loading}
          items={displayedItems}
          onSelect={openDetails}
          onOpenDetails={openDetails}
          selectable={selectMode}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
        />

        <MediaDetailsDrawer
          open={drawerOpen}
          asset={selected}
          onClose={closeDetails}
          onSave={handleSaveMeta}
          onDelete={handleDelete}
        />

        <BulkTagDialog
          open={tagDialogOpen}
          count={selectedAssets.length}
          tagOptions={tagOptions}
          onClose={() => setTagDialogOpen(false)}
          onApply={applyBulkTags}
        />
      </Stack>

      {dragging ? (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal,
            bgcolor: 'rgba(15, 23, 42, 0.45)',
            display: 'grid',
            placeItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography sx={{ color: '#fff', fontSize: 22, fontWeight: 900 }}>
            Drop images or videos to upload
          </Typography>
        </Box>
      ) : null}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2500}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
